import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Send, Plus, Trash2, Eye, BarChart3, Users, CheckCircle, XCircle, 
  Ban, MousePointerClick, Copy, Pause, Play, Calendar, Download,
  Image, Video, Music, FileText, DollarSign, TrendingUp, RefreshCw,
  Upload, X, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { 
  useBroadcasts, useCreateBroadcast, useUpdateBroadcast, useDeleteBroadcast,
  useSendBroadcast, useEstimateRecipients, useSendTestBroadcast, type Broadcast
} from "@/hooks/useBroadcasts";

const statusLabels: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Черновик", variant: "secondary" },
  scheduled: { label: "Запланирована", variant: "outline" },
  sending: { label: "Отправляется", variant: "default" },
  paused: { label: "Пауза", variant: "outline" },
  completed: { label: "Завершена", variant: "default" },
  cancelled: { label: "Отменена", variant: "destructive" },
};

interface BroadcastButton {
  text: string;
  url: string;
  callback_data: string;
}

export default function Broadcasts() {
  const { data: broadcasts, isLoading, refetch } = useBroadcasts();
  const createBroadcast = useCreateBroadcast();
  const updateBroadcast = useUpdateBroadcast();
  const deleteBroadcast = useDeleteBroadcast();
  const sendBroadcast = useSendBroadcast();
  const sendTestBroadcast = useSendTestBroadcast();
  const estimateRecipients = useEstimateRecipients();

  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("list");

  // Form state
  const [name, setName] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messageFormat, setMessageFormat] = useState("html");
  const [buttons, setButtons] = useState<BroadcastButton[]>([]);
  const [sendType, setSendType] = useState("instant");
  const [scheduledAt, setScheduledAt] = useState("");
  const [mediaType, setMediaType] = useState<string>("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mediaFileName, setMediaFileName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Segment filters
  const [segAll, setSegAll] = useState(true);
  const [hasPurchased, setHasPurchased] = useState<boolean | undefined>(undefined);
  const [utmSource, setUtmSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [minGenerations, setMinGenerations] = useState(0);
  const [registeredAfter, setRegisteredAfter] = useState("");
  const [registeredBefore, setRegisteredBefore] = useState("");

  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [testTelegramId, setTestTelegramId] = useState("");
  const [testUsername, setTestUsername] = useState("");

  // Load admin telegram username from settings
  const { data: adminTgUsername } = useQuery({
    queryKey: ['settings', 'admin_telegram_username'],
    queryFn: async () => {
      const { data } = await supabase.from('settings').select('value').eq('key', 'admin_telegram_username').maybeSingle();
      return data?.value as string | null;
    },
  });

  useEffect(() => {
    if (adminTgUsername && !testUsername && !testTelegramId) {
      const val = String(adminTgUsername).replace(/"/g, '');
      if (val) setTestUsername(val);
    }
  }, [adminTgUsername]);

  // Realtime subscription for sending broadcasts
  useEffect(() => {
    const channel = supabase
      .channel('broadcasts-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'broadcasts' }, () => {
        refetch();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  const resetForm = () => {
    setName(""); setMessageText(""); setMessageFormat("html");
    setButtons([]); setSendType("instant"); setScheduledAt("");
    setSegAll(true); setHasPurchased(undefined); setUtmSource("");
    setUtmCampaign(""); setMinGenerations(0); setRegisteredAfter("");
    setRegisteredBefore(""); setEstimatedCount(null);
    setMediaType(""); setMediaUrl(""); setMediaFileName("");
  };

  const buildFilter = () => {
    if (segAll) return {};
    const f: Record<string, any> = {};
    if (hasPurchased !== undefined) f.has_purchased = hasPurchased;
    if (utmSource) f.utm_source = utmSource;
    if (utmCampaign) f.utm_campaign = utmCampaign;
    if (minGenerations > 0) f.min_generations = minGenerations;
    if (registeredAfter) f.registered_after = registeredAfter;
    if (registeredBefore) f.registered_before = registeredBefore;
    return f;
  };

  const handleEstimate = async () => {
    try {
      const count = await estimateRecipients.mutateAsync(buildFilter());
      setEstimatedCount(count);
    } catch {
      toast.error("Ошибка оценки аудитории");
    }
  };

  const handleTestSend = async () => {
    const identifier = testUsername.trim() || testTelegramId.trim();
    if (!identifier || !messageText.trim()) {
      toast.error("Укажите username или Telegram ID и текст сообщения");
      return;
    }
    try {
      await sendTestBroadcast.mutateAsync({
        telegram_id: /^\d+$/.test(identifier) ? Number(identifier) : undefined,
        username: /^\d+$/.test(identifier) ? undefined : identifier.replace('@', ''),
        message_text: messageText,
        message_format: messageFormat,
        media_type: mediaType && mediaType !== 'none' ? mediaType : null,
        media_url: mediaUrl || null,
        buttons: buttons.filter(b => b.text),
      });
      toast.success("Тестовое сообщение отправлено!");
    } catch (e: any) {
      toast.error(`Ошибка: ${e.message}`);
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !messageText.trim()) {
      toast.error("Заполните название и текст");
      return;
    }
    try {
      await createBroadcast.mutateAsync({
        name: name.trim(),
        message_text: messageText,
        message_format: messageFormat,
        buttons: buttons.length > 0 ? buttons : [],
        segment_filter: buildFilter(),
        send_type: sendType,
        scheduled_at: sendType === 'scheduled' && scheduledAt ? scheduledAt : null,
        media_type: mediaType || null,
        media_url: mediaUrl || null,
        status: 'draft',
      } as any);
      toast.success("Рассылка создана");
      setShowCreate(false);
      resetForm();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleSend = async (id: string) => {
    try {
      await sendBroadcast.mutateAsync(id);
      toast.success("Рассылка запущена!");
      setConfirmSend(null);
    } catch (e: any) {
      toast.error(`Ошибка: ${e.message}`);
    }
  };

  const handleDuplicate = async (b: Broadcast) => {
    try {
      await createBroadcast.mutateAsync({
        name: `${b.name} (копия)`,
        message_text: b.message_text,
        message_format: b.message_format,
        buttons: b.buttons,
        segment_filter: b.segment_filter,
        send_type: 'instant',
        media_type: b.media_type,
        media_url: b.media_url,
        status: 'draft',
      } as any);
      toast.success("Рассылка скопирована");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handlePause = async (id: string) => {
    await updateBroadcast.mutateAsync({ id, status: 'paused' } as any);
    toast.success("Рассылка приостановлена");
  };

  const handleResume = async (id: string) => {
    await updateBroadcast.mutateAsync({ id, status: 'sending' } as any);
    toast.success("Рассылка возобновлена");
  };

  const handleCancel = async (id: string) => {
    await updateBroadcast.mutateAsync({ id, status: 'cancelled' } as any);
    toast.success("Рассылка отменена");
  };

  const handleExportCSV = () => {
    if (!broadcasts?.length) return;
    const header = "Название,Статус,Получатели,Доставлено,Ошибки,Блокировки,Клики,CTR%,Покупки,Выручка₽,Дата\n";
    const rows = broadcasts.map(b => {
      const ctr = b.delivered_count > 0 ? Math.round((b.clicked_count / b.delivered_count) * 100) : 0;
      return `"${b.name}",${b.status},${b.total_recipients},${b.delivered_count},${b.failed_count},${b.blocked_count},${b.clicked_count},${ctr},${b.purchases_count},${b.revenue_rub},${format(new Date(b.created_at), 'dd.MM.yyyy HH:mm')}`;
    }).join("\n");
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `broadcasts_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV экспортирован");
  };

  const detailBroadcast = broadcasts?.find(b => b.id === showDetail);

  // Summary stats
  const totalSent = broadcasts?.filter(b => b.status === 'completed').reduce((s, b) => s + b.delivered_count, 0) || 0;
  const totalClicks = broadcasts?.reduce((s, b) => s + b.clicked_count, 0) || 0;
  const totalRevenue = broadcasts?.reduce((s, b) => s + b.revenue_rub, 0) || 0;
  const totalPurchases = broadcasts?.reduce((s, b) => s + b.purchases_count, 0) || 0;
  const avgCTR = totalSent > 0 ? Math.round((totalClicks / totalSent) * 100) : 0;

  const sendingBroadcasts = broadcasts?.filter(b => b.status === 'sending') || [];

  return (
    <AdminLayout title="Рассылки" description="Массовая отправка сообщений через Telegram-бота">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="list">Рассылки</TabsTrigger>
            <TabsTrigger value="analytics">Аналитика</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!broadcasts?.length}>
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button onClick={() => { resetForm(); setShowCreate(true); }}>
              <Plus className="w-4 h-4 mr-1" /> Создать
            </Button>
          </div>
        </div>

        {/* Sending progress */}
        {sendingBroadcasts.length > 0 && (
          <div className="space-y-2">
            {sendingBroadcasts.map(b => {
              const progress = b.total_recipients > 0 
                ? Math.round(((b.sent_count + b.failed_count + b.blocked_count) / b.total_recipients) * 100) 
                : 0;
              return (
                <Card key={b.id} className="border-primary/30 bg-primary/5">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                        <span className="font-medium text-sm">{b.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {b.sent_count}/{b.total_recipients} • ❌{b.failed_count} • 🚫{b.blocked_count}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => handlePause(b.id)}>
                          <Pause className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleCancel(b.id)}>
                          <XCircle className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <div className="text-xs text-muted-foreground mt-1">{progress}%</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <TabsContent value="list" className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-2xl font-bold">{broadcasts?.length || 0}</div>
                <div className="text-xs text-muted-foreground">Рассылок</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-2xl font-bold">{totalSent}</div>
                <div className="text-xs text-muted-foreground">Доставлено</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-2xl font-bold">{avgCTR}%</div>
                <div className="text-xs text-muted-foreground">Сред. CTR</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-2xl font-bold">{totalPurchases}</div>
                <div className="text-xs text-muted-foreground">Покупок</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-2xl font-bold">{totalRevenue}₽</div>
                <div className="text-xs text-muted-foreground">Выручка</div>
              </CardContent>
            </Card>
          </div>

          {/* Broadcasts table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Получ.</TableHead>
                    <TableHead className="text-right">Доставл.</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Покупки</TableHead>
                    <TableHead className="text-right">Выручка</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Загрузка...</TableCell></TableRow>
                  ) : !broadcasts?.length ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Нет рассылок</TableCell></TableRow>
                  ) : broadcasts.map(b => {
                    const ctr = b.delivered_count > 0 ? Math.round((b.clicked_count / b.delivered_count) * 100) : 0;
                    const st = statusLabels[b.status] || { label: b.status, variant: "secondary" as const };
                    return (
                      <TableRow key={b.id}>
                        <TableCell className="font-medium max-w-[180px] truncate">
                          {b.media_type && (
                            <span className="mr-1">
                              {b.media_type === 'photo' && '🖼️'}
                              {b.media_type === 'video' && '🎬'}
                              {b.media_type === 'gif' && '🎞️'}
                              {b.media_type === 'audio' && '🎵'}
                            </span>
                          )}
                          {b.name}
                        </TableCell>
                        <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                        <TableCell className="text-right">{b.total_recipients}</TableCell>
                        <TableCell className="text-right">{b.delivered_count}</TableCell>
                        <TableCell className="text-right">{ctr}%</TableCell>
                        <TableCell className="text-right">{b.purchases_count}</TableCell>
                        <TableCell className="text-right">{b.revenue_rub > 0 ? `${b.revenue_rub}₽` : '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(b.created_at), 'dd.MM.yy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {b.status === 'draft' && (
                              <Button size="icon" variant="ghost" onClick={() => setConfirmSend(b.id)} title="Отправить">
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {b.status === 'sending' && (
                              <Button size="icon" variant="ghost" onClick={() => handlePause(b.id)} title="Пауза">
                                <Pause className="w-4 h-4" />
                              </Button>
                            )}
                            {b.status === 'paused' && (
                              <Button size="icon" variant="ghost" onClick={() => handleResume(b.id)} title="Продолжить">
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" onClick={() => setShowDetail(b.id)} title="Детали">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDuplicate(b)} title="Копировать">
                              <Copy className="w-4 h-4" />
                            </Button>
                            {(b.status === 'draft' || b.status === 'cancelled') && (
                              <Button size="icon" variant="ghost" onClick={() => {
                                if (confirm('Удалить рассылку?')) deleteBroadcast.mutate(b.id);
                              }} title="Удалить">
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top performing broadcasts */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Топ по CTR</CardTitle>
              </CardHeader>
              <CardContent>
                {broadcasts?.filter(b => b.status === 'completed' && b.delivered_count > 0)
                  .sort((a, b) => (b.clicked_count / b.delivered_count) - (a.clicked_count / a.delivered_count))
                  .slice(0, 5)
                  .map(b => {
                    const ctr = Math.round((b.clicked_count / b.delivered_count) * 100);
                    return (
                      <div key={b.id} className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
                        <span className="text-sm truncate max-w-[200px]">{b.name}</span>
                        <Badge variant={ctr > 10 ? "default" : "secondary"}>{ctr}%</Badge>
                      </div>
                    );
                  }) || <div className="text-sm text-muted-foreground">Нет данных</div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Топ по выручке</CardTitle>
              </CardHeader>
              <CardContent>
                {broadcasts?.filter(b => b.revenue_rub > 0)
                  .sort((a, b) => b.revenue_rub - a.revenue_rub)
                  .slice(0, 5)
                  .map(b => (
                    <div key={b.id} className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
                      <span className="text-sm truncate max-w-[200px]">{b.name}</span>
                      <span className="font-semibold text-sm">{b.revenue_rub}₽</span>
                    </div>
                  )) || <div className="text-sm text-muted-foreground">Нет данных</div>}
              </CardContent>
            </Card>

            {/* Overall stats */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Сводка по всем рассылкам</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Конверсия в покупку</div>
                    <div className="text-xl font-bold">
                      {totalClicks > 0 ? Math.round((totalPurchases / totalClicks) * 100) : 0}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Средний чек</div>
                    <div className="text-xl font-bold">
                      {totalPurchases > 0 ? Math.round(totalRevenue / totalPurchases) : 0}₽
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Выручка на получателя</div>
                    <div className="text-xl font-bold">
                      {totalSent > 0 ? (totalRevenue / totalSent).toFixed(1) : 0}₽
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Блокировок бота</div>
                    <div className="text-xl font-bold">
                      {broadcasts?.reduce((s, b) => s + b.blocked_count, 0) || 0}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Создать рассылку</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название (внутреннее)</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Промо акция март" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Формат</Label>
                <Select value={messageFormat} onValueChange={setMessageFormat}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="html">HTML</SelectItem>
                    <SelectItem value="markdown">Markdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Тип отправки</Label>
                <Select value={sendType} onValueChange={setSendType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">Мгновенная</SelectItem>
                    <SelectItem value="scheduled">Запланированная</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {sendType === 'scheduled' && (
              <div>
                <Label>Дата и время (МСК)</Label>
                <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
              </div>
            )}

            <div>
              <Label>Текст сообщения</Label>
              <Textarea 
                value={messageText} 
                onChange={e => setMessageText(e.target.value)} 
                placeholder="<b>Привет!</b> 🎵&#10;&#10;У нас новая акция..."
                rows={6}
              />
            </div>

            {/* Media upload */}
            <div className="space-y-3">
              <Label>Медиа</Label>
              <Select value={mediaType} onValueChange={v => { setMediaType(v); if (v === 'none' || !v) { setMediaUrl(''); setMediaFileName(''); } }}>
                <SelectTrigger><SelectValue placeholder="Без медиа" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Без медиа</SelectItem>
                  <SelectItem value="photo">🖼️ Фото</SelectItem>
                  <SelectItem value="video">🎬 Видео</SelectItem>
                  <SelectItem value="gif">🎞️ GIF</SelectItem>
                  <SelectItem value="audio">🎵 Аудио</SelectItem>
                </SelectContent>
              </Select>

              {mediaType && mediaType !== 'none' && (
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={
                      mediaType === 'photo' ? 'image/*' :
                      mediaType === 'video' ? 'video/*' :
                      mediaType === 'gif' ? 'image/gif' :
                      mediaType === 'audio' ? 'audio/*' : '*/*'
                    }
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploading(true);
                      try {
                        const ext = file.name.split('.').pop();
                        const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
                        const { error } = await supabase.storage
                          .from('broadcast-media')
                          .upload(path, file);
                        if (error) throw error;
                        const { data: { publicUrl } } = supabase.storage
                          .from('broadcast-media')
                          .getPublicUrl(path);
                        setMediaUrl(publicUrl);
                        setMediaFileName(file.name);
                        toast.success("Файл загружен");
                      } catch (err: any) {
                        toast.error(`Ошибка загрузки: ${err.message}`);
                      } finally {
                        setUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />

                  {mediaUrl ? (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-muted">
                      {mediaType === 'photo' && <Image className="w-4 h-4 text-muted-foreground" />}
                      {mediaType === 'video' && <Video className="w-4 h-4 text-muted-foreground" />}
                      {mediaType === 'gif' && <Image className="w-4 h-4 text-muted-foreground" />}
                      {mediaType === 'audio' && <Music className="w-4 h-4 text-muted-foreground" />}
                      <span className="text-sm truncate flex-1">{mediaFileName || 'Файл'}</span>
                      {mediaType === 'photo' && (
                        <img src={mediaUrl} alt="" className="w-10 h-10 rounded object-cover" />
                      )}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => { setMediaUrl(''); setMediaFileName(''); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Загрузка...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Прикрепить {
                          mediaType === 'photo' ? 'фото' :
                          mediaType === 'video' ? 'видео' :
                          mediaType === 'gif' ? 'GIF' :
                          mediaType === 'audio' ? 'аудио' : 'файл'
                        }</>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Inline-кнопки</Label>
                <Button size="sm" variant="outline" onClick={() => setButtons([...buttons, { text: '', url: '', callback_data: '' }])}>
                  <Plus className="w-3 h-3 mr-1" /> Кнопка
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                🔗 URL — открывает ссылку. 🤖 Команда — вызывает действие в боте (buy, new_hit, buy:5)
              </p>
              {buttons.map((btn, idx) => {
                const isUrlMode = !!(btn.url && btn.url.startsWith('http'));
                const btnType = isUrlMode ? 'url' : 'callback';
                const btnValue = isUrlMode ? btn.url : (btn.callback_data || '');
                return (
                  <div key={idx} className="flex gap-2 mb-2 items-center">
                    <Input 
                      placeholder="Текст кнопки" 
                      value={btn.text} 
                      onChange={e => {
                        const nb = [...buttons];
                        nb[idx] = { ...nb[idx], text: e.target.value };
                        setButtons(nb);
                      }}
                      className="flex-[2]"
                    />
                    <Select
                      value={btnType}
                      onValueChange={v => {
                        const nb = [...buttons];
                        if (v === 'url') {
                          nb[idx] = { ...nb[idx], url: '', callback_data: '' };
                        } else {
                          nb[idx] = { ...nb[idx], callback_data: '', url: '' };
                        }
                        setButtons(nb);
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="callback">🤖 Команда</SelectItem>
                        <SelectItem value="url">🔗 URL</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      placeholder={btnType === 'url' ? 'https://...' : 'buy, new_hit, buy:5'}
                      value={btnValue}
                      onChange={e => {
                        const nb = [...buttons];
                        if (btnType === 'url') {
                          nb[idx] = { ...nb[idx], url: e.target.value, callback_data: '' };
                        } else {
                          nb[idx] = { ...nb[idx], callback_data: e.target.value, url: '' };
                        }
                        setButtons(nb);
                      }}
                      className="flex-[3]"
                    />
                    <Button size="icon" variant="ghost" onClick={() => setButtons(buttons.filter((_, i) => i !== idx))}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Segmentation */}
            <div>
              <Label className="text-base font-semibold">Сегментация</Label>
              <div className="flex items-center gap-2 mt-2">
                <Switch checked={segAll} onCheckedChange={v => { setSegAll(v); setEstimatedCount(null); }} />
                <span className="text-sm">Все пользователи</span>
              </div>
            </div>

            {!segAll && (
              <div className="space-y-3 pl-2 border-l-2 border-primary/20">
                <div>
                  <Label className="text-xs">Покупатели</Label>
                  <Select value={hasPurchased === undefined ? 'all' : hasPurchased ? 'yes' : 'no'} onValueChange={v => {
                    setHasPurchased(v === 'all' ? undefined : v === 'yes');
                    setEstimatedCount(null);
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      <SelectItem value="yes">Только покупатели</SelectItem>
                      <SelectItem value="no">Только НЕ покупали</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">UTM Source</Label>
                    <Input value={utmSource} onChange={e => { setUtmSource(e.target.value); setEstimatedCount(null); }} placeholder="telegram, vk..." />
                  </div>
                  <div>
                    <Label className="text-xs">UTM Campaign</Label>
                    <Input value={utmCampaign} onChange={e => { setUtmCampaign(e.target.value); setEstimatedCount(null); }} placeholder="spring_2026..." />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Мин. генераций</Label>
                  <Input type="number" value={minGenerations} onChange={e => { setMinGenerations(Number(e.target.value)); setEstimatedCount(null); }} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Зарег. после</Label>
                    <Input type="date" value={registeredAfter} onChange={e => { setRegisteredAfter(e.target.value); setEstimatedCount(null); }} />
                  </div>
                  <div>
                    <Label className="text-xs">Зарег. до</Label>
                    <Input type="date" value={registeredBefore} onChange={e => { setRegisteredBefore(e.target.value); setEstimatedCount(null); }} />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleEstimate} disabled={estimateRecipients.isPending}>
                <Users className="w-3 h-3 mr-1" /> Оценить аудиторию
              </Button>
              {estimatedCount !== null && (
                <span className="text-sm font-medium">≈ {estimatedCount} получателей</span>
              )}
            </div>

            <Separator />

            {/* Preview */}
            {messageText && (
              <Card className="bg-muted/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-xs">Предпросмотр</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  {mediaUrl && mediaType && mediaType !== 'none' && (
                    <div className="mb-2 text-xs text-muted-foreground flex items-center gap-1">
                      {mediaType === 'photo' && <Image className="w-3 h-3" />}
                      {mediaType === 'video' && <Video className="w-3 h-3" />}
                      {mediaType === 'audio' && <Music className="w-3 h-3" />}
                      <span>Медиа: {mediaUrl.substring(0, 40)}...</span>
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: messageText }} />
                  {buttons.filter(b => b.text).map((btn, i) => (
                    <div key={i} className="mt-1">
                      <Badge variant="outline" className="text-xs">{btn.text} {btn.url && '↗'}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          <Separator />

           <div className="space-y-2">
            <Label className="text-sm font-semibold">Тестовая отправка</Label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1 flex-1">
                <span className="text-muted-foreground text-sm">@</span>
                <Input
                  placeholder="username или Telegram ID"
                  value={testUsername || testTelegramId}
                  onChange={e => {
                    const v = e.target.value;
                    if (/^\d+$/.test(v)) { setTestTelegramId(v); setTestUsername(""); }
                    else { setTestUsername(v.replace('@', '')); setTestTelegramId(""); }
                  }}
                  className="flex-1"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleTestSend}
                disabled={sendTestBroadcast.isPending || !messageText.trim() || (!testTelegramId.trim() && !testUsername.trim())}
              >
                {sendTestBroadcast.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Отправка...</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" /> Тест</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Отправит это сообщение вам в Telegram для проверки перед массовой рассылкой
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button onClick={handleCreate} disabled={createBroadcast.isPending}>
              Создать черновик
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm send dialog */}
      <Dialog open={!!confirmSend} onOpenChange={() => setConfirmSend(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердить отправку</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Рассылка будет отправлена всем пользователям из выбранного сегмента. Прогресс отображается в реальном времени.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSend(null)}>Отмена</Button>
            <Button onClick={() => confirmSend && handleSend(confirmSend)} disabled={sendBroadcast.isPending}>
              <Send className="w-4 h-4 mr-1" /> Отправить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailBroadcast?.name}</DialogTitle>
          </DialogHeader>
          {detailBroadcast && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Получатели</div>
                    <div className="font-semibold">{detailBroadcast.total_recipients}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Доставлено</div>
                    <div className="font-semibold">{detailBroadcast.delivered_count}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Ошибки</div>
                    <div className="font-semibold">{detailBroadcast.failed_count}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-orange-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">Блокировки</div>
                    <div className="font-semibold">{detailBroadcast.blocked_count}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MousePointerClick className="w-4 h-4 text-blue-500" />
                  <div>
                    <div className="text-xs text-muted-foreground">CTR</div>
                    <div className="font-semibold">
                      {detailBroadcast.clicked_count}
                      {detailBroadcast.delivered_count > 0 && (
                        <span className="text-muted-foreground ml-1 text-xs">
                          ({Math.round((detailBroadcast.clicked_count / detailBroadcast.delivered_count) * 100)}%)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <div>
                    <div className="text-xs text-muted-foreground">Покупки</div>
                    <div className="font-semibold">{detailBroadcast.purchases_count}</div>
                  </div>
                </div>
              </div>

              {/* Revenue & ROI */}
              {(detailBroadcast.revenue_rub > 0 || detailBroadcast.purchases_count > 0) && (
                <Card className="bg-primary/5">
                  <CardContent className="py-3 px-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <div className="text-xs text-muted-foreground">Выручка</div>
                        <div className="text-lg font-bold">{detailBroadcast.revenue_rub}₽</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Средний чек</div>
                        <div className="text-lg font-bold">
                          {detailBroadcast.purchases_count > 0 
                            ? Math.round(detailBroadcast.revenue_rub / detailBroadcast.purchases_count) 
                            : 0}₽
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Конверсия</div>
                        <div className="text-lg font-bold">
                          {detailBroadcast.delivered_count > 0 
                            ? ((detailBroadcast.purchases_count / detailBroadcast.delivered_count) * 100).toFixed(1) 
                            : 0}%
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Separator />

              <div>
                <div className="text-xs text-muted-foreground mb-1">Текст сообщения:</div>
                <div className="text-sm bg-muted/50 rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {detailBroadcast.message_text}
                </div>
              </div>

              {detailBroadcast.media_type && detailBroadcast.media_url && (
                <div className="text-xs text-muted-foreground">
                  Медиа: {detailBroadcast.media_type} — <a href={detailBroadcast.media_url} target="_blank" className="underline">{detailBroadcast.media_url.substring(0, 50)}...</a>
                </div>
              )}

              {detailBroadcast.started_at && (
                <div className="text-xs text-muted-foreground">
                  Начало: {format(new Date(detailBroadcast.started_at), 'dd MMM yyyy HH:mm', { locale: ru })}
                  {detailBroadcast.completed_at && (
                    <> → Конец: {format(new Date(detailBroadcast.completed_at), 'HH:mm')}</>
                  )}
                </div>
              )}

              {/* Segment info */}
              {detailBroadcast.segment_filter && Object.keys(detailBroadcast.segment_filter).length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Сегмент:</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(detailBroadcast.segment_filter).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-xs">{k}: {String(v)}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
